import { useLocale } from '@bees-web/core/shared/contexts'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import { removeParamsFrom, urlToSearchParams } from '@/core/utils/url'

import {
  AUTH_FAILURE_QUERY_PARAM,
  LOGIN_ERROR_DETAILED_MESSAGES,
  LOGIN_ERROR_MESSAGES_BY_LANGUAGE,
} from '../locales/loginErrorMessages'

import type { NextRouter } from 'next/router'

function getLoginErrorMessage(
  code: string | null | undefined,
  language: string | undefined
): string {
  const stage = process.env.STAGE?.toLocaleLowerCase() || 'prod'
  const isNonProd = stage !== 'prod'

  if (isNonProd) {
    return (
      LOGIN_ERROR_DETAILED_MESSAGES[code ?? ''] ?? LOGIN_ERROR_DETAILED_MESSAGES.generic
    )
  }

  return (
    LOGIN_ERROR_MESSAGES_BY_LANGUAGE[language] ?? LOGIN_ERROR_MESSAGES_BY_LANGUAGE.en
  )
}

function getLoginErrorCode(query: NextRouter['query']): string | null {
  if (AUTH_FAILURE_QUERY_PARAM !== 'login_error' || !('login_error' in query)) {
    return null
  }

  const errorCode = query.login_error
  if (!errorCode) return null
  if (Array.isArray(errorCode)) return errorCode[0] ?? null
  return errorCode
}

function removeLoginErrorFromUrl(search: string): string | null {
  if (!search) return null

  const params = urlToSearchParams(search)
  if (!params.has(AUTH_FAILURE_QUERY_PARAM)) return null

  removeParamsFrom(params, AUTH_FAILURE_QUERY_PARAM)
  const nextSearch = params.toString()
  return nextSearch ? `?${nextSearch}` : ''
}

function removeLoginErrorFromQuery(
  query: NextRouter['query']
): NextRouter['query'] | null {
  if (!(AUTH_FAILURE_QUERY_PARAM in query)) return null

  const { [AUTH_FAILURE_QUERY_PARAM]: _, ...rest } = query
  return rest
}

function replaceUrlWithoutLoginError(router: NextRouter): void {
  const nextQuery = removeLoginErrorFromQuery(router.query)
  if (nextQuery === null) return

  router.replace({ pathname: router.pathname, query: nextQuery }, undefined, {
    shallow: true,
  })
}

export function useLoginErrorNotification() {
  const router = useRouter()
  const { language } = useLocale()
  const [isShowing, setIsShowing] = useState(false)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!router.isReady) return

    const errorCode = getLoginErrorCode(router.query)
    if (!errorCode) return

    setMessage(getLoginErrorMessage(errorCode, language))
    setIsShowing(true)
    replaceUrlWithoutLoginError(router)
  }, [language, router, router.isReady, router.query])

  const handleClose = useCallback(() => {
    setIsShowing(false)
  }, [])

  return {
    isShowing,
    message,
    handleClose,
  }
}

export {
  getLoginErrorCode,
  removeLoginErrorFromQuery,
  removeLoginErrorFromUrl,
  replaceUrlWithoutLoginError,
}
